/* ns-motion-pdp — NordBench Pro product page motion (premium motion pass).
   Marks elements with the .nsm-* classes ns-motion-pdp.css defines, reveals
   them on scroll, and runs the two counters. Bails out under reduced motion,
   where html.ns-motion-ok is absent and every held state in the stylesheet is
   inert.

   The trigger is one rAF-throttled sweep, not an IntersectionObserver:
   Chrome reports an empty intersection rect for any element carrying a
   clip-path, so the clip-based reveals (.nsm-clip, .nsm-pod) never fire
   through an observer. getBoundingClientRect ignores clip-path, and the
   sweep also covers horizontal carousels, whose items arrive through their
   own scroller rather than the page's. */
(function () {
  var M = window.NSMotion;
  if (!M || M.reduced) return;

  var CAP = 9; // stagger index cap, matches the CSS delay ladder
  var watched = [];
  var ticking = false;

  // `now` skips the stagger delay, for reveals that answer a keypress rather
  // than a scroll position.
  function show(el, now) {
    if (!el || el.classList.contains('is-inview')) return;
    if (now) el.classList.add('nsm-now');
    el.classList.add('is-inview');
    // Once the entrance has played, hover and press states must answer at
    // their own speed, not at the entrance's duration and stagger delay.
    setTimeout(function () {
      el.classList.add('nsm-settled');
    }, 1600);
    if (el.__nsmNums) el.__nsmNums.forEach(countUp);
    // Choreographed children ride their parent, not their own scroll position,
    // so a run of ticks reads as one sequence.
    if (el.__nsmKids) {
      var kids = el.querySelectorAll(el.__nsmKids);
      for (var i = 0; i < kids.length; i++) show(kids[i], now);
    }
  }

  function watch(el, index, passive) {
    if (!el || el.__nsm) return;
    el.__nsm = true;
    if (index != null) el.style.setProperty('--ns-motion-i', Math.min(index, CAP));
    if (passive) return; // revealed by an ancestor, never by its own scroll
    watched.push(el);
  }

  // row: [selector, classes, stagger, baseIndex, passive, childSelector]
  function mark(row) {
    var list = document.querySelectorAll(row[0]);
    for (var i = 0; i < list.length; i++) {
      if (row[1]) list[i].classList.add.apply(list[i].classList, row[1].split(' '));
      if (row[5]) list[i].__nsmKids = row[5];
      watch(list[i], row[2] ? (row[3] || 0) + i : row[3], row[4]);
    }
  }

  // Wrap a heading's children so its text can rise out from behind its own
  // bottom edge without editing the section markup.
  function maskify(selector, index) {
    var list = document.querySelectorAll(selector);
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.__nsmMask || !el.firstChild) continue;
      el.__nsmMask = true;
      var inner = document.createElement('span');
      inner.className = 'nsm-mask__i';
      while (el.firstChild) inner.appendChild(el.firstChild);
      el.appendChild(inner);
      el.classList.add('nsm-mask');
      watch(el, index);
    }
  }

  // Wrap each number in its own span, trailing punctuation included so the
  // span stays one unbreakable token on the line.
  function wrapNumbers(el, re) {
    if (!el || el.__nsmNums) return;
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null),
      nodes = [],
      found = [],
      node;
    while ((node = walker.nextNode())) nodes.push(node);
    nodes.forEach(function (n) {
      var text = n.nodeValue,
        frag = document.createDocumentFragment(),
        last = 0,
        m;
      re.lastIndex = 0;
      while ((m = re.exec(text))) {
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        var span = document.createElement('span');
        span.className = 'nsm-num';
        span.textContent = m[0];
        span.setAttribute('data-nsm-to', m[1].replace(/,/g, ''));
        span.setAttribute('data-nsm-tpl', m[0].replace(m[1], '\u0000'));
        frag.appendChild(span);
        found.push(span);
        last = m.index + m[0].length;
      }
      if (!last) return;
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      n.parentNode.replaceChild(frag, n);
    });
    if (!found.length) return;
    // Pin the finished width so counting can never reflow the heading.
    found.forEach(function (sp) {
      sp.style.minWidth = Math.ceil(sp.getBoundingClientRect().width) + 'px';
    });
    el.__nsmNums = found;
  }

  function countUp(span) {
    if (span.__nsmRan) return;
    span.__nsmRan = true;
    var to = parseInt(span.getAttribute('data-nsm-to'), 10),
      tpl = span.getAttribute('data-nsm-tpl'),
      start = 0;
    if (!to || to > 1000000) return;
    function frame(now) {
      if (!start) start = now;
      var p = Math.min((now - start) / 1000, 1);
      span.textContent = tpl.replace('\u0000', String(Math.round((1 - Math.pow(1 - p, 3)) * to)));
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  /* The page score, top down. [selector, classes, stagger, base]; an empty
     class means the stylesheet holds that element already (the hero) and JS
     only gives it a place in the delay ladder. */
  var DECK = [
    ['.product-info__badge-list', ''],
    ['.product-info__title', '', 0, 1],
    ['.product-gallery__thumbnail', '', 1, 1],
    ['.product-info__price', '', 0, 4],
    ['.flash-deal-price-container', '', 0, 5],
    ['.product-info__separator', '', 0, 6],
    ['.product-info__description', '', 0, 7],
    ['.product-gallery__zoom', '', 0, 8],
    ['.callouts-wrapper > *', 'nsm-up', 1],
    ['.product-info__accordion', 'nsm-up', 1],
    ['.ns-exercise-tabs-carousel__tab-single', 'nsm-pop', 1],
    ['.ns-exercise-tabs-carousel-gallery__wrapper', 'nsm-clip'],
    ['.ns-exercise-tabs-carousel-gallery__thumbnails', 'nsm-up', 0, 2],
    ['.nsct3__col--labels', 'nsm-left'],
    ['.nsct3__col--featured', 'nsm-pod', 0, 0, 0, '.nsm-pop'],
    ['.nsct3__cell--featured svg', 'nsm-pop', 1, 2, 1],
    ['.nsct3__slider', 'nsm-fade', 0, 3],
    ['.ns-faqwi__intro', 'nsm-up'],
    ['.ns-faqwi__media', 'nsm-clip'],
    ['.ns-faqwi__item', 'nsm-up'],
    ['.ns-gtb__head > :not(h2)', 'nsm-up', 0, 1],
    ['.ns-gtb__tab', 'nsm-pop', 1],
    ['.ns-gtb__video', 'nsm-clip'],
    ['.ns-gtb__text > *', 'nsm-up', 1, 1],
    ['.ns-rwf2__description', 'nsm-up', 0, 1],
    ['.ns-rwf2__filter-btn', 'nsm-pop', 1],
    ['.ns-rwf2__card', 'nsm-up'],
    ['.ns-rwf2__footer', 'nsm-up'],
    ['section-header.section-header', 'nsm-up'],
    ['.multi-column__item', 'nsm-up', 1],
    ['.multi-column__item img', 'nsm-img'],
    ['.nsbg__item', 'nsm-clip', 1],
    ['.nsbg__media img', 'nsm-img']
  ];

  // Headings that rise out from behind their own baseline.
  var MASKS = [
    '.ns-exercise-tabs-carousel__container .ns-section-header__heading h2',
    '.nsct3__heading',
    '.ns-gtb__head h2',
    '.ns-rwf2__heading',
    '.nsbg__heading'
  ];

  function build() {
    wrapNumbers(document.querySelector('.nsct3__heading'), /\$(\d[\d,]*)[.,!?)]*/g);
    wrapNumbers(document.querySelector('.ns-rwf2__description'), /(\d[\d,]*)%[.,!?)]*/g);
    for (var m = 0; m < MASKS.length; m++) maskify(MASKS[m], 0);
    for (var i = 0; i < DECK.length; i++) mark(DECK[i]);
  }

  /* One layout read per element per frame, all before any class write, and
     the list shrinks as elements land. Anything already scrolled past — down
     the page or across a carousel — lands at once rather than staying held
     behind the viewer. A zero-size rect (an inactive tab panel) stays held;
     the control handler below reveals those. */
  function sweep() {
    var edge = window.innerHeight * 0.9,
      right = window.innerWidth,
      rest = [],
      hit = [],
      i;
    for (i = 0; i < watched.length; i++) {
      var r = watched[i].getBoundingClientRect();
      if (!r.width && !r.height) {
        rest.push(watched[i]);
      } else if (
        r.bottom <= 0 ||
        r.right <= 0 ||
        (r.top < edge && r.bottom > 0 && r.left < right && r.right > 0)
      ) {
        hit.push(watched[i]);
      } else {
        rest.push(watched[i]);
      }
    }
    watched = rest;
    for (i = 0; i < hit.length; i++) show(hit[i]);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      sweep();
    });
  }

  // Tabs, filters and pagination can swap in content the sweep never saw,
  // because it was display:none. Reveal it and replay the panel.
  var CONTROLS =
    '.ns-exercise-tabs-carousel__tab-single, .ns-gtb__tab, .ns-rwf2__filter-btn,' +
    ' .ns-rwf2__more, .nsct3__prev, .nsct3__next, .nsbg__toggle, .ns-faqwi__toggle';
  var HELD = '.nsm-up, .nsm-pop, .nsm-clip, .nsm-fade, .nsm-left, .nsm-pod, .nsm-mask';

  function swap(panel) {
    if (!panel) return;
    panel.classList.remove('nsm-swap');
    void panel.offsetWidth;
    panel.classList.add('nsm-swap');
  }

  document.addEventListener(
    'click',
    function (e) {
      var control = e.target.closest && e.target.closest(CONTROLS);
      if (!control) return;
      // Re-clicking the tab that is already open changes nothing, so it must
      // not replay the panel entrance. This listener runs in capture, before
      // the section's own handler, so the state read here is the pre-click one.
      if (
        control.getAttribute('aria-selected') === 'true' ||
        (control.matches('.ns-exercise-tabs-carousel__tab-single') && control.classList.contains('active'))
      ) {
        return;
      }
      var section = control.closest('.shopify-section');
      setTimeout(function () {
        if (section) {
          var held = section.querySelectorAll(HELD);
          for (var i = 0; i < held.length; i++) {
            if (held[i].offsetParent !== null) show(held[i]);
          }
        }
        if (control.matches('.ns-gtb__tab')) {
          swap(document.getElementById(control.getAttribute('aria-controls')));
        } else if (control.matches('.ns-exercise-tabs-carousel__tab-single')) {
          swap(control.closest('.ns-exercise-tabs-carousel__wrapper'));
        } else if (control.matches('.ns-rwf2__filter-btn')) {
          swap(document.querySelector('.ns-rwf2__grid'));
        }
      }, 60);
    },
    true
  );

  /* The sweep has a bottom dead zone (0.9 of the viewport), and the browser's
     own focus scroll parks a newly focused control right inside it — so
     tabbing could land on a control still held at opacity 0, with its focus
     ring invisible (WCAG 2.4.7, 2.4.11). Focus outranks scroll position:
     reveal the focused element and every held ancestor at once. */
  document.addEventListener('focusin', function (e) {
    var n = e.target;
    while (n && n !== document.body) {
      if (n.__nsm) show(n, true);
      n = n.parentElement;
    }
  });

  // Two frames, so the held state paints once and the reveal transitions
  // instead of snapping to its finished value.
  function settle() {
    requestAnimationFrame(function () {
      requestAnimationFrame(sweep);
    });
  }

  build();
  settle();
  // Capture, because a carousel's own scroll event does not bubble.
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', function () {
    build();
    settle();
  });
  window.addEventListener('load', function () {
    setTimeout(sweep, 900);
  });
})();
